import 'reflect-metadata';
import { TranslationMetadataKey, TranslationMeta } from './translatable.decorator';
import { TransPropMetadataKey } from './trans-prop.decorator';
import { TranslateTreeFunc } from '../..';

export function getDeepModelMetadata(rootClass: Function, path: string): TranslationMeta | undefined {
    const cleanedPath = path
        .replace(/\[\d+\]/g, '')
        .replace(/\.\d+(?=\.|$)/g, '');
    const pathParts = cleanedPath.split('.');

    let currentClass: Function = rootClass;

    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        const prototype = currentClass.prototype;

        const isLast = i === pathParts.length - 1;

        if (isLast) {
            return Reflect.getMetadata(TranslationMetadataKey, prototype, part) as TranslationMeta | undefined;
        }

        let designType = Reflect.getMetadata('design:type', prototype, part);

        // For map-like wrappers (e.g. guideCaps.<index>.left/right.name),
        // left/right are not real fields on element type and should be skipped.
        if (!designType && (part === 'left' || part === 'right')) {
            continue;
        }

        if (designType === Array || designType === Object) {
            const elementType = Reflect.getMetadata(TransPropMetadataKey, prototype, part);
            if (!elementType) {
                return undefined;
            }
            currentClass = elementType;
            continue;
        }

        if (!designType || typeof designType !== 'function' || !designType.prototype) {
            return undefined;
        }

        currentClass = designType;
    }

    return undefined;
}

export function getTranslationKeyForField(rootClass: Function, path: string) {
    const fieldMeta = getDeepModelMetadata(rootClass, path);

    if (fieldMeta) {
        return fieldMeta.model;
    }
    return null;
}

export function translateConfiguration(rootClass: Function, config: any, dataTranslated: any[], translate?: TranslateTreeFunc): any {
    const pickTranslationValue = (row: any) => {
        return row?.content ?? null;
    };

    const findTranslation = (model: string, foreignKey: any, field: string) => {
        if (foreignKey === undefined || foreignKey === null) {
            return null;
        }
        return dataTranslated.find((r) =>
            r.model === model &&
            (r.foreign_key == foreignKey || r.foreign_key === foreignKey) &&
            r.field === field
        );
    };

    const getPossibleId = (obj: any) => {
        if (!obj || typeof obj !== 'object') {
            return undefined;
        }
        return obj.id || obj.driveId || undefined;
    };

    const recurse = (current: any, currentPath: string): any => {
        if (current === null || typeof current === 'undefined') {
            return current;
        }

        // primitive
        if (typeof current !== 'object') {
            const meta = getDeepModelMetadata(rootClass as any, currentPath);
            if (meta) {
                const parentPath = currentPath.replace(/\.[^.]+$/, '');
            }
            return current;
        }

        // array -> recurse each element
        if (Array.isArray(current)) {
            return current.map((el, idx) => {
                const arrPath = `${currentPath}[${idx}]`;
                return recurse(el, arrPath);
            });
        }

        // object -> iterate keys
        const out: any = Array.isArray(current) ? [] : {};
        for (const key of Object.keys(current)) {
            const value = current[key];
            const path = currentPath ? `${currentPath}.${key}` : key;

            const meta = getDeepModelMetadata(rootClass as any, path);
            if (meta) {
                const idField = meta.idField || 'id';
                if (current[idField] === undefined || current[idField] === null || current[idField] === -1) {
                    if (translate) {
                        out[key] = translate('INTERFACE|Brak');
                        continue;
                    }
                }
                const foreignKey = current[idField];
                const row = findTranslation(meta.model, foreignKey, meta.field || key);
                const translated = pickTranslationValue(row);
                out[key] = (translated !== null && typeof translated !== 'undefined') ? translated : value;
                continue;
            }

            // if value is object/array -> recurse deeper
            if (value !== null && typeof value === 'object') {
                out[key] = recurse(value, path);
                continue;
            }

            out[key] = value;
        }

        return out;
    };

    return recurse(config, '');
}
